<template>
  <div
    :class="[
      { 'full-height--adjust': activeTab === 0  },
      { 'full-height--partil-adjust': activeTab > 0 }
    ]"
    style="min-height: 3px"
  >
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="isValidArray(items)" class="table">
          <v-layout class="table__header pl-0" align-center>
            <v-flex class="td--action" xs1 />
            <v-flex style="min-width: 130px;">ID</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.date`) }}</v-flex>
            <v-flex xs3>{{ $t(`${namespace}.employeeName`) }}</v-flex>
            <v-flex xs2>{{ $t(`${namespace}.costCenter`) }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.category`) }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.distance`) }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.duration`) }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.value`) }}</v-flex>
          </v-layout>

          <div v-if="!summarize" class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.ridesThisMonth`) }}</span>
          </div>

          <div
            v-if="summarize && summarizeNumberCount && summarizeValue"
            class="table__summarize-status table__summarize-status--warning"
          >
            <strong>{{ summarizeNumberCount | formatNumber }}</strong> {{ $t(`${namespace}.summarizeChargebackText`) }} <strong class="success--text">{{ summarizeValue | currencyBRL }}</strong>
          </div>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile class="table__body__tr pl-0">
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex class="td--action table__td--prevent text-xs-center" xs1>
                        <v-menu
                          v-if="item.chargeBack && (hasRole('billing', 'ROLE_PUT_CHARGEBACK') || hasRole('billing', 'ROLE_DELETE_CHARGEBACK'))"
                          offset-y
                          right
                        >
                          <button class="table-body__actions" slot="activator">
                            <v-icon>more_vertical</v-icon>
                          </button>

                          <v-list class="dropdown-menu-actions__list">
                            <v-list-tile
                              v-if="hasRole('billing', 'ROLE_PUT_CHARGEBACK')"
                              @click="handleEditChargebackRide(item)"
                            >
                              <v-list-tile-title>{{ $t(`${namespace}.editRideChargeback`) }}</v-list-tile-title>
                            </v-list-tile>

                            <v-divider v-if="hasRole('billing', 'ROLE_PUT_CHARGEBACK') && hasRole('billing', 'ROLE_DELETE_CHARGEBACK')" />

                            <v-list-tile
                              v-if="hasRole('billing', 'ROLE_DELETE_CHARGEBACK')"
                              @click="handleDeleteChargeback(item)"
                            >
                              <v-list-tile-title>{{ $t(`${namespace}.deleteRideChargeback`) }}</v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>

                        <v-tooltip
                          v-if="!item.chargeBack && hasRole('billing', 'ROLE_POST_CHARGEBACK')"
                          top
                          slot="activator"
                          max-width="172px"
                        >
                          <v-btn
                            slot="activator"
                            flat
                            icon
                            class="dropdown-actions__activator"
                            @click="handleAddChargebackRide(item)"
                          >
                            <v-icon color="gray-6">local_offer</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.reverseRide`) }}</span>
                        </v-tooltip>
                      </v-flex>

                      <v-flex style="min-width: 130px;">
                        <span
                          v-if="
                            item.receipt &&
                            item.receipt.corporateJob &&
                            item.receipt.corporateJob.apiInfo &&
                            item.receipt.corporateJob.apiInfo.apiTaxiJobId
                          "
                          class="text--ellipsis"
                          contenteditable
                        >
                          {{ item.receipt.corporateJob.apiInfo.apiTaxiJobId }}
                        </span>
                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs2>
                        <span
                          v-if="
                            item.receipt &&
                            item.receipt.corporateJob &&
                            item.receipt.corporateJob.start &&
                            item.receipt.corporateJob.start.date &&
                            item.receipt.corporateJob.end &&
                            item.receipt.corporateJob.end.date
                          "
                        >
                          <span
                            :title="item.receipt.corporateJob.start.date | formatDate('DD/MM/YYYY')"
                            class="text--ellipsis"
                          >
                            {{ item.receipt.corporateJob.start.date | formatDate('DD/MM/YYYY') }}
                          </span>
                          <span class="gray-6--text caption text--ellipsis">
                            {{ $t(`${namespace}.from`) }} {{ item.receipt.corporateJob.start.date | formatDate('HH:mm') }}
                            {{ $t(`${namespace}.to`) }} {{ item.receipt.corporateJob.end.date | formatDate('HH:mm') }}
                          </span>
                        </span>
                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs3>
                        <span
                          v-if="
                            item.receipt &&
                            item.receipt.corporateJob &&
                            item.receipt.corporateJob.employee &&
                            item.receipt.corporateJob.employee.name
                          "
                          :title="item.receipt.corporateJob.employee.name"
                          class="text--ellipsis"
                        >
                          {{ item.receipt.corporateJob.employee.name | ellipsis(25) }}
                        </span>
                        <span class="text--ellipsis" v-else>-</span>

                        <span
                          v-if="
                            item.receipt &&
                            item.receipt.corporateJob &&
                            item.receipt.corporateJob.employee &&
                            item.receipt.corporateJob.employee.email
                          "
                          :title="item.receipt.corporateJob.employee.email"
                          class="gray-6--text caption text--ellipsis"
                        >
                          {{ item.receipt.corporateJob.employee.email }}
                        </span>
                        <span v-else class="gray-6--text caption text--ellipsis">-</span>
                      </v-flex>

                      <v-flex xs2>
                        <span
                          v-if="
                            item.receipt &&
                            item.receipt.corporateJob &&
                            item.receipt.corporateJob.costCenter &&
                            item.receipt.corporateJob.costCenter.name
                          "
                          :title="item.receipt.corporateJob.costCenter.name"
                          class="text--ellipsis"
                        >
                          {{ item.receipt.corporateJob.costCenter.name | ellipsis(22) }}
                        </span>
                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs1>
                        <span
                          v-if="
                            item.receipt &&
                            item.receipt.corporateJob &&
                            item.receipt.corporateJob.taxiCategoryName
                          "
                          v-html="getRideCategory(item.receipt.corporateJob.taxiCategoryName)"
                          :title="item.receipt.corporateJob.taxiCategoryName"
                          class="text--ellipsis"
                        />
                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs1>
                        <span
                          v-if="item.distance"
                          :title="item.distance | metresToKm"
                        >
                          {{ item.distance | metresToKm }}
                        </span>
                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs1>
                        <span
                          v-if="
                            item.receipt &&
                            item.receipt.corporateJob &&
                            item.receipt.corporateJob.start &&
                            item.receipt.corporateJob.start.date &&
                            item.receipt.corporateJob.end &&
                            item.receipt.corporateJob.end.date
                          "
                          :title="item.receipt.corporateJob.start.date | durationCalc(item.receipt.corporateJob.end.date)"
                        >
                          {{ item.receipt.corporateJob.start.date | durationCalc(item.receipt.corporateJob.end.date) }}
                        </span>
                        <span v-else>-</span>
                      </v-flex>

                      <v-flex xs1>
                        <span v-if="item.amount && item.chargeBack && item.chargeBack.value">
                          <span
                            class="text--ellipsis"
                            :title="(item.amount - item.chargeBack.value) | currencyBRL"
                          >
                            {{ (item.amount - item.chargeBack.value) | currencyBRL }}
                          </span>

                          <span
                            class="gray-6--text caption text--ellipsis line-through"
                            :title="item.amount | currencyBRL"
                          >
                            {{ item.amount | currencyBRL }}
                          </span>
                        </span>

                        <span
                          v-else-if="item.amount"
                          class="text--ellipsis"
                          :title="item.amount | currencyBRL"
                        >
                          {{ item.amount | currencyBRL }}
                        </span>

                        <span v-else>-</span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-layout
                v-if="item.chargeBack && Number(item.chargeBack.value) < Number(item.amount)"
                class="chargeback chargeback--partial"
                align-center
              >
                <v-flex class="td--action table__td--prevent text-xs-center" xs1 />

                <v-flex style="min-width: 130px;">
                  <strong :title="$t(`${namespace}.partialChargeback`)" class="chargeback__text text--ellipsis">
                    {{ $t(`${namespace}.partialChargeback`) }}
                  </strong>
                </v-flex>

                <v-flex xs2>
                  <span
                    v-if="item.chargeBack.createdDate"
                    class="chargeback__text text--ellipsis"
                  >
                    {{ item.chargeBack.createdDate | formatDate('DD/MM/YYYY') }} {{ $t(`${namespace}.to`) }} {{ item.chargeBack.createdDate | formatDate('HH:mm') }}
                  </span>
                  <span v-else>-</span>
                </v-flex>

                <v-flex xs3>
                  <span class="chargeback__text text--ellipsis">
                    {{ $t(`${namespace}.per`) }} <span class="text--capitalize">
                      <span v-if="item.chargeBack.createdBy && item.chargeBack.createdBy.name">
                        {{ item.chargeBack.createdBy.name }}
                      </span>
                      <span v-else>-</span>
                    </span>
                  </span>
                </v-flex>

                <v-flex xs5>
                  <span class="chargeback__text text--ellipsis">
                    <strong>{{ $t(`${namespace}.justification`) }}:</strong>
                    {{ item.chargeBack.reason || '-' }}
                  </span>
                </v-flex>

                <v-flex xs1>
                  <span v-if="item.chargeBack.value" class="font-weight--semibold success--text">
                    {{ item.chargeBack.value | currencyBRL }}
                  </span>
                  <span v-else>-</span>
                </v-flex>
              </v-layout>

              <v-layout
                v-if=" item.chargeBack && Number(item.chargeBack.value) === Number(item.amount)"
                class="chargeback chargeback--total"
                align-center
              >
                <v-flex class="td--action table__td--prevent text-xs-center" xs1 />

                <v-flex style="min-width: 130px;">
                  <strong :title="$t(`${namespace}.totalChargeback`)" class="chargeback__text text--ellipsis">
                    {{ $t(`${namespace}.totalChargeback`) }}
                  </strong>
                </v-flex>

                <v-flex xs2>
                  <span v-if="item.chargeBack.createdDate">
                    {{ item.chargeBack.createdDate | formatDate('DD/MM/YYYY') }} {{ $t(`${namespace}.to`) }} {{ item.chargeBack.createdDate | formatDate('HH:mm') }}
                  </span>
                  <span v-else>-</span>
                </v-flex>

                <v-flex xs3>
                  <span class="chargeback__text text--ellipsis">
                    {{ $t(`${namespace}.per`) }} <span class="text--capitalize">
                      <span v-if="item.chargeBack.createdBy && item.chargeBack.createdBy.name">
                        {{ item.chargeBack.createdBy.name }}
                      </span>
                      <span v-else>-</span>
                    </span>
                  </span>
                </v-flex>

                <v-flex xs5>
                  <span class="chargeback__text text--ellipsis">
                    <strong>{{ $t(`${namespace}.justification`) }}:</strong>
                    {{ item.chargeBack.reason || '-' }}
                  </span>
                </v-flex>

                <v-flex xs1>
                  <span v-if="item.chargeBack.value" class="font-weight--semibold success--text">
                    {{ item.chargeBack.value | currencyBRL }}
                  </span>
                  <span v-else>-</span>
                </v-flex>
              </v-layout>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="usage-policy"
          :text="activeTab === 0 ? $t(`${namespace}.ridesEmptyState`) : $t(`${namespace}.chargebackEmptyState`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, formatNumber, getRideCategory, hasRole } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'TableRide',
  props: {
    items: {
      type: Array,
      required: true
    },
    summarizeNumberCount: {
      type: Number,
      default: 0
    },
    summarizeValue: {
      type: Number,
      default: 0
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    summarize: {
      type: Boolean,
      default: false
    },
    activeTab: {
      type: Number
    }
  },
  components: {
    EmptyState
  },
  computed: {
    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.FinancialAdjustmentsPage'
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidArray (val) {
      return isArray(val)
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    getRideCategory (category) {
      return getRideCategory(this, category).text
    },

    handleAddChargebackRide (item) {
      this.$emit('addChargebackRide', {
        type: 'create',
        data: item
      })
    },

    handleEditChargebackRide (item) {
      this.$emit('editChargebackRide', {
        type: 'edit',
        data: item
      })
    },

    handleDeleteChargeback (item) {
      this.$emit('deleteChargeback', item)
    }
  }
}
</script>

<style lang="scss" scoped>
.full-height--partil-adjust {
  height: calc(100% - 69px);
}

.full-height--adjust {
  height: calc(100% - 69px - 64px);
}

.table__title {
  align-items: center;
  border-bottom: 1px solid rgba(0,0,0,0.12);
  display: flex;
  font-size: 14px;
  font-weight: 600;
  padding: 0 16px;
  height: 40px;
}
</style>

<style lang="scss">
.chargeback {
  /* border-top: 1px solid $gray-3; */
  height: 32px;
  padding-right: 16px;
  position: relative;

  &:before,
  &:after {
    content: '';
    position: absolute;
  }

  &:before {
    border-bottom: 8px solid #eee;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid transparent;
    bottom: 100%;
    display: block;
    height: 10px;
    left: 18px;
    width: 10px;
  }
}

.chargeback--partial,
.chargeback--total {

  &:after {
    bottom: -1px;
    left: 0;
    height: 1px;
    right: 0;
    z-index: 1;
  }
}

.chargeback--partial {
  background-color: #fff0d8;

  &:after {
    background-color: #ff9d00;
  }

  &:before {
    border-bottom: 8px solid #ff9d00;
  }
}

.chargeback--total {
  background-color: $attention;

  &:after {
    background-color: #ffd900;
  }

  &:before {
    border-bottom: 8px solid #ffd900;
  }
}

.chargeback__text {
  font-size: 12px;
}

.line-through {
  color: $gray-4;
  text-decoration:line-through;
}

.table__summarize-status {
  border-bottom: 1px solid $gray-4;
  text-align: center;
  padding: 20px 0;

  &.table__summarize-status--warning {
    background-color: $attention;
  }
}
</style>
